import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import {
  Upload,
  Download,
  ChevronDown,
  FileSpreadsheet,
  Loader2,
  CheckCircle,
  AlertCircle,
  Plus,
  Sparkles,
  Package,
  Store,
  Link,
  Trash2,
  RefreshCw,
  Star,
  ExternalLink,
  AlertTriangle,
  Pencil,
  Image,
  Save,
  X,
  Clock,
  Timer,
  Hash,
  Globe,
  ImageIcon,
  Bell,
  Send,
  Calendar,
  CheckCircle2,
} from 'lucide-react';
import { toast } from 'sonner';
import { apiFetch } from '../utils/api';
import BrandAliasesCard from '../components/BrandAliasesCard';
import { useAuth } from '../context/AuthContext';

const platformOptions = [
  { value: 'Amazon', label: 'Amazon', icon: '🛒' },
  { value: 'Flipkart', label: 'Flipkart', icon: '📦' },
  { value: 'Blinkit', label: 'Blinkit', icon: '⚡' },
  { value: 'Zepto', label: 'Zepto', icon: '🚀' },
  { value: 'Wellversed.in', label: 'Wellversed.in (D2C)', icon: '🌿' },
];

export default function SettingsPage() {
  const { user } = useAuth();
  const isAdmin = !!user?.is_admin;
  const [sendingAlertFor, setSendingAlertFor] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadResult, setUploadResult] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [selectedPlatform, setSelectedPlatform] = useState('');

  // URL Tracking state
  const [trackedUrls, setTrackedUrls] = useState([]);
  const [loadingUrls, setLoadingUrls] = useState(false);
  const [newTracking, setNewTracking] = useState({ platform: '', brand: '', url: '' });
  const [addingTracking, setAddingTracking] = useState(false);
  const [fetchingUrl, setFetchingUrl] = useState(null);
  const [editingUrl, setEditingUrl] = useState(null);
  const [editUrlData, setEditUrlData] = useState({ platform: '', brand: '', url: '' });
  const [trackedUrlsExpanded, setTrackedUrlsExpanded] = useState(false);
  const [trackedSkuUrlsExpanded, setTrackedSkuUrlsExpanded] = useState(false);
  const [oscRatingsExpanded, setOscRatingsExpanded] = useState(false);
  const [globalUrlsExpanded, setGlobalUrlsExpanded] = useState(false);

  // Brands & Platforms state
  const [brands, setBrands] = useState([]);
  const [platforms, setPlatforms] = useState([]);
  const [brandStats, setBrandStats] = useState({});
  const [loadingBrands, setLoadingBrands] = useState(false);
  const [loadingPlatforms, setLoadingPlatforms] = useState(false);
  const [showBrandDialog, setShowBrandDialog] = useState(false);
  const [showPlatformDialog, setShowPlatformDialog] = useState(false);
  const [editingBrand, setEditingBrand] = useState(null);
  const [editingPlatform, setEditingPlatform] = useState(null);
  const [brandForm, setBrandForm] = useState({ name: '', logo_url: '', manager_email: '', rating_threshold: '', alert_frequency: 'daily', alert_day_of_week: 0, alert_hour_ist: 9, channel_stakeholders: {} });
  const [brandChannels, setBrandChannels] = useState([]);  // platforms with tracked SKUs for the brand being edited
  const [showRoutingInfo, setShowRoutingInfo] = useState(false);
  const [platformForm, setPlatformForm] = useState({ name: '', icon_url: '' });

  // SKU URL Tracking state
  const [skuUrls, setSkuUrls] = useState([]);
  const [loadingSkuUrls, setLoadingSkuUrls] = useState(false);
  const [newSkuTracking, setNewSkuTracking] = useState({ platform: '', brand: '', sku_name: '', url: '' });
  const [addingSkuTracking, setAddingSkuTracking] = useState(false);
  const [fetchingSkuUrl, setFetchingSkuUrl] = useState(null);
  const [editingSkuUrl, setEditingSkuUrl] = useState(null);
  const [editSkuUrlData, setEditSkuUrlData] = useState({ platform: '', brand: '', sku_name: '', url: '' });

  // Manual Platform Ratings state (Blinkit, Zepto, etc.)
  const [manualRatings, setManualRatings] = useState([]);
  const [loadingManualRatings, setLoadingManualRatings] = useState(false);
  const [newManualRating, setNewManualRating] = useState({ brand: '', platform: '', sku_name: '', total_rating_count: '', average_rating: '' });
  const [addingManualRating, setAddingManualRating] = useState(false);
  const [editingManualRating, setEditingManualRating] = useState(null);
  const [editManualRatingData, setEditManualRatingData] = useState({ brand: '', platform: '', sku_name: '', total_rating_count: '', average_rating: '' });
  const [uploadingManualBulk, setUploadingManualBulk] = useState(false);
  const manualBulkFileRef = useRef(null);
  const [uploadingUrlBulk, setUploadingUrlBulk] = useState(false);
  const urlBulkFileRef = useRef(null);
  const [uploadingSkuBulk, setUploadingSkuBulk] = useState(false);
  const skuBulkFileRef = useRef(null);

  // Grouped Products state
  const [groupedProducts, setGroupedProducts] = useState({ brands: [], total_products: 0, total_brands: 0 });
  const [expandedBrands, setExpandedBrands] = useState({});
  const [showProductDialog, setShowProductDialog] = useState(false);
  const [editProduct, setEditProduct] = useState({ brand: '', sku_name: '', platform_urls: {}, platform_ratings: {}, image_url: '' });
  const [savingProduct, setSavingProduct] = useState(false);
  const [loadingGroupedProducts, setLoadingGroupedProducts] = useState(false);

  const MANUAL_PLATFORMS = ['Blinkit'];

  // Meta posts state
  const [metaPosts, setMetaPosts] = useState([]);
  const [metaPostUrl, setMetaPostUrl] = useState('');
  const [metaPostBrand, setMetaPostBrand] = useState('');
  const [metaPostLabel, setMetaPostLabel] = useState('');
  const [addingMetaPost, setAddingMetaPost] = useState(false);
  const [fetchingMeta, setFetchingMeta] = useState({});

  // Auto-fetch state
  const [autoFetchInterval, setAutoFetchInterval] = useState(0);
  const [metaAutoFetchInterval, setMetaAutoFetchInterval] = useState(2);
  const [savingMetaAutoFetch, setSavingMetaAutoFetch] = useState(false);
  const [lastGlobalFetch, setLastGlobalFetch] = useState(null);
  const [savingAutoFetch, setSavingAutoFetch] = useState(false);

  // Load data on mount
  useEffect(() => {
    fetchTrackedUrls();
    fetchSkuUrls();
    fetchManualRatings();
    fetchBrands();
    fetchPlatforms();
    fetchBrandStats();
    fetchAutoFetchSettings();
    fetchMetaAutoFetch();
    fetchGroupedProducts();
    fetchMetaPosts();
  }, []);

  const fetchGroupedProducts = async () => {
    setLoadingGroupedProducts(true);
    try {
      const res = await apiFetch('/api/products/grouped', { credentials: 'include' });
      if (res.ok) setGroupedProducts(await res.json());
    } catch (err) { console.error('Error fetching grouped products:', err); }
    finally { setLoadingGroupedProducts(false); }
  };

  const saveProduct = async () => {
    const trimmedName = (editProduct.sku_name || '').trim();
    if (!editProduct.brand || !trimmedName) {
      toast.error('Brand and product name are required');
      return;
    }
    setSavingProduct(true);
    try {
      const endpoint = editProduct.isEdit ? '/api/products/update' : '/api/products/add';
      const method = editProduct.isEdit ? 'PUT' : 'POST';
      const isRename = editProduct.isEdit && editProduct.original_sku_name && trimmedName !== editProduct.original_sku_name;
      const payload = {
        brand: editProduct.brand,
        sku_name: editProduct.isEdit ? (editProduct.original_sku_name || trimmedName) : trimmedName,
        platform_urls: editProduct.platform_urls,
        platform_ratings: editProduct.platform_ratings,
        image_url: editProduct.image_url || '',
      };
      if (isRename) payload.new_sku_name = trimmedName;
      const res = await apiFetch(endpoint, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        toast.success(editProduct.isEdit ? (isRename ? 'Product renamed & updated' : 'Product updated') : 'Product added');
        setShowProductDialog(false);
        setEditProduct({ brand: '', sku_name: '', platform_urls: {}, platform_ratings: {}, image_url: '' });
        await fetchGroupedProducts();
      } else {
        let msg = 'Failed to save product';
        try { const body = await res.json(); if (body?.detail) msg = body.detail; } catch (e) { /* ignore */ }
        toast.error(msg);
      }
    } catch (err) {
      toast.error('Failed to save product');
    } finally {
      setSavingProduct(false);
    }
  };

  const fetchMetaPosts = async () => {
    try {
      const res = await apiFetch('/api/meta/posts');
      if (res.ok) setMetaPosts(await res.json());
    } catch (err) { console.error(err); }
  };

  const addMetaPost = async () => {
    if (!metaPostBrand || !metaPostUrl) { toast.error('Brand and URL required'); return; }
    setAddingMetaPost(true);
    try {
      const res = await apiFetch('/api/meta/posts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ brand: metaPostBrand, url: metaPostUrl, label: metaPostLabel }),
      });
      if (res.ok) {
        toast.success('Meta post added');
        setMetaPostUrl(''); setMetaPostLabel('');
        fetchMetaPosts();
      } else { toast.error('Failed to add'); }
    } catch (err) { toast.error('Failed to add'); }
    setAddingMetaPost(false);
  };

  const fetchMetaComments = async (postId) => {
    setFetchingMeta(prev => ({ ...prev, [postId]: true }));
    try {
      const res = await apiFetch(`/api/meta/posts/${postId}/fetch`, { method: 'POST' });
      if (res.ok) {
        const data = await res.json();
        if (data.inserted > 0) {
          toast.success(`Fetched ${data.inserted} new comments`);
        } else if (data.hint) {
          toast.error(data.hint);
        } else {
          toast.info(data.message || 'No new comments found');
        }
        fetchMetaPosts();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to fetch comments');
      }
    } catch (err) { toast.error('Failed to fetch comments'); }
    setFetchingMeta(prev => ({ ...prev, [postId]: false }));
  };

  const deleteMetaPost = async (postId) => {
    try {
      await apiFetch(`/api/meta/posts/${postId}`, { method: 'DELETE' });
      toast.success('Post removed');
      fetchMetaPosts();
    } catch (err) { toast.error('Failed to delete'); }
  };


  const fetchAutoFetchSettings = async () => {
    try {
      const res = await apiFetch('/api/settings/auto-fetch', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setAutoFetchInterval(data.interval_hours || 0);
        setLastGlobalFetch(data.last_global_fetch || null);
      }
    } catch (err) { console.error(err); }
  };

  const updateAutoFetchInterval = async (hours) => {
    setSavingAutoFetch(true);
    try {
      const res = await apiFetch('/api/settings/auto-fetch', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ interval_hours: hours }),
      });
      if (res.ok) {
        setAutoFetchInterval(hours);
        toast.success(hours > 0 ? `Auto-fetch set to every ${hours} hour${hours > 1 ? 's' : ''}` : 'Auto-fetch disabled');
      } else {
        toast.error('Failed to update auto-fetch settings');
      }
    } catch (err) {
      toast.error('Failed to update auto-fetch settings');
    } finally {
      setSavingAutoFetch(false);
    }
  };

  const fetchMetaAutoFetch = async () => {
    try {
      const res = await apiFetch('/api/settings/meta-auto-fetch');
      if (res.ok) {
        const data = await res.json();
        setMetaAutoFetchInterval(data.interval_hours ?? 2);
      }
    } catch (err) { console.error(err); }
  };

  const updateMetaAutoFetch = async (hours) => {
    setSavingMetaAutoFetch(true);
    try {
      const res = await apiFetch('/api/settings/meta-auto-fetch', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ interval_hours: hours }),
      });
      if (res.ok) {
        setMetaAutoFetchInterval(hours);
        toast.success(hours > 0 ? `Meta stats will refresh every ${hours} hour${hours > 1 ? 's' : ''}` : 'Meta auto-refresh disabled');
      } else {
        toast.error('Failed to update Meta auto-fetch');
      }
    } catch {
      toast.error('Failed to update Meta auto-fetch');
    } finally {
      setSavingMetaAutoFetch(false);
    }
  };

  const fetchTrackedUrls = async () => {
    setLoadingUrls(true);
    try {
      const response = await apiFetch('/api/scraper/urls', { credentials: 'include' });
      if (response.ok) {
        setTrackedUrls(await response.json());
      }
    } catch (err) {
      console.error('Error fetching tracked URLs:', err);
    } finally {
      setLoadingUrls(false);
    }
  };

  const addTrackingUrl = async () => {
    if (!newTracking.platform || !newTracking.brand || !newTracking.url) {
      toast.error('Please fill in all fields');
      return;
    }
    setAddingTracking(true);
    try {
      const response = await apiFetch('/api/scraper/urls', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          brand: newTracking.brand,
          platform: newTracking.platform,
          product_name: newTracking.brand,
          url: newTracking.url,
        }),
      });
      if (response.ok) {
        toast.success('URL added for tracking');
        setNewTracking({ platform: '', brand: '', url: '' });
        await fetchTrackedUrls();
      } else {
        toast.error('Failed to add URL');
      }
    } catch (err) {
      toast.error('Failed to add URL');
    } finally {
      setAddingTracking(false);
    }
  };

  const deleteTrackingUrl = async (urlId) => {
    try {
      await apiFetch(`/api/scraper/urls/${urlId}`, {
        method: 'DELETE',
      });
      toast.success('URL removed');
      await fetchTrackedUrls();
    } catch (err) {
      toast.error('Failed to delete URL');
    }
  };

  const fetchSingleUrl = async (urlId) => {
    setFetchingUrl(urlId);
    try {
      const response = await apiFetch(`/api/scraper/fetch/${urlId}`, {
        method: 'POST',
      });
      if (response.ok) {
        const result = await response.json();
        if (result.status === 'success') {
          toast.success(`Fetched: Rating ${result.rating}, ${result.review_count?.toLocaleString()} reviews`);
        } else if (result.status === 'blocked') {
          toast.error('Platform is blocking automated requests. Try again later.');
        } else {
          toast.error('Could not fetch data from this URL');
        }
        await fetchTrackedUrls();
      }
    } catch (err) {
      toast.error('Fetch failed');
    } finally {
      setFetchingUrl(null);
    }
  };

  const startEditUrl = (item) => {
    setEditingUrl(item.url_id);
    setEditUrlData({ platform: item.platform, brand: item.brand, url: item.url });
  };

  const saveEditUrl = async (urlId) => {
    try {
      const response = await apiFetch(`/api/scraper/urls/${urlId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editUrlData),
      });
      if (response.ok) {
        toast.success('URL updated');
        setEditingUrl(null);
        await fetchTrackedUrls();
      } else {
        toast.error('Failed to update URL');
      }
    } catch (err) {
      toast.error('Failed to update');
    }
  };

  // SKU URL Tracking handlers
  const fetchSkuUrls = async () => {
    setLoadingSkuUrls(true);
    try {
      const response = await apiFetch('/api/scraper/sku-urls', { credentials: 'include' });
      if (response.ok) {
        setSkuUrls(await response.json());
      }
    } catch (err) {
      console.error('Error fetching SKU URLs:', err);
    } finally {
      setLoadingSkuUrls(false);
    }
  };

  const addSkuTrackingUrl = async () => {
    if (!newSkuTracking.platform || !newSkuTracking.brand || !newSkuTracking.sku_name || !newSkuTracking.url) {
      toast.error('Please fill in all fields');
      return;
    }
    setAddingSkuTracking(true);
    try {
      const response = await apiFetch('/api/scraper/sku-urls', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          brand: newSkuTracking.brand,
          platform: newSkuTracking.platform,
          sku_name: newSkuTracking.sku_name,
          url: newSkuTracking.url,
        }),
      });
      if (response.ok) {
        toast.success('SKU URL added for tracking');
        setNewSkuTracking({ platform: '', brand: '', sku_name: '', url: '' });
        await fetchSkuUrls();
      } else {
        toast.error('Failed to add SKU URL');
      }
    } catch (err) {
      toast.error('Failed to add SKU URL');
    } finally {
      setAddingSkuTracking(false);
    }
  };

  const deleteSkuTrackingUrl = async (urlId) => {
    try {
      await apiFetch(`/api/scraper/sku-urls/${urlId}`, { method: 'DELETE' });
      toast.success('SKU URL removed');
      await fetchSkuUrls();
    } catch (err) {
      toast.error('Failed to delete SKU URL');
    }
  };

  const fetchSingleSkuUrl = async (urlId) => {
    setFetchingSkuUrl(urlId);
    try {
      const response = await apiFetch(`/api/scraper/sku-fetch/${urlId}`, { method: 'POST' });
      if (response.ok) {
        const result = await response.json();
        if (result.status === 'success') {
          toast.success(`Fetched: Rating ${result.last_rating}, ${result.last_review_count?.toLocaleString()} reviews`);
        } else if (result.status === 'blocked') {
          toast.error('Platform is blocking automated requests. Try again later.');
        } else {
          toast.error('Could not fetch data from this URL');
        }
        await fetchSkuUrls();
      }
    } catch (err) {
      toast.error('Fetch failed');
    } finally {
      setFetchingSkuUrl(null);
    }
  };

  const startEditSkuUrl = (item) => {
    setEditingSkuUrl(item.url_id);
    setEditSkuUrlData({ platform: item.platform, brand: item.brand, sku_name: item.sku_name, url: item.url });
  };

  const saveEditSkuUrl = async (urlId) => {
    try {
      const response = await apiFetch(`/api/scraper/sku-urls/${urlId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editSkuUrlData),
      });
      if (response.ok) {
        toast.success('SKU URL updated');
        setEditingSkuUrl(null);
        await fetchSkuUrls();
      } else {
        toast.error('Failed to update SKU URL');
      }
    } catch (err) {
      toast.error('Failed to update');
    }
  };

  // Manual Platform Ratings handlers (Blinkit, Zepto)
  const fetchManualRatings = async () => {
    setLoadingManualRatings(true);
    try {
      const response = await apiFetch('/api/manual-ratings');
      if (response.ok) setManualRatings(await response.json());
    } catch (err) {
      console.error('Error fetching manual ratings:', err);
    } finally {
      setLoadingManualRatings(false);
    }
  };

  const addManualRating = async () => {
    if (!newManualRating.brand || !newManualRating.platform || !newManualRating.sku_name || !newManualRating.total_rating_count || !newManualRating.average_rating) {
      toast.error('Please fill in all fields');
      return;
    }
    setAddingManualRating(true);
    try {
      const response = await apiFetch('/api/manual-ratings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          brand: newManualRating.brand,
          platform: newManualRating.platform,
          sku_name: newManualRating.sku_name,
          total_rating_count: parseInt(newManualRating.total_rating_count),
          average_rating: parseFloat(newManualRating.average_rating),
        }),
      });
      if (response.ok) {
        toast.success('Rating added');
        setNewManualRating({ brand: '', platform: '', sku_name: '', total_rating_count: '', average_rating: '' });
        await fetchManualRatings();
      } else {
        toast.error('Failed to add rating');
      }
    } catch (err) {
      toast.error('Failed to add rating');
    } finally {
      setAddingManualRating(false);
    }
  };

  const deleteManualRating = async (ratingId) => {
    try {
      await apiFetch(`/api/manual-ratings/${ratingId}`, { method: 'DELETE' });
      toast.success('Rating removed');
      await fetchManualRatings();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const startEditManualRating = (item) => {
    setEditingManualRating(item.rating_id);
    setEditManualRatingData({
      brand: item.brand,
      platform: item.platform,
      sku_name: item.sku_name,
      total_rating_count: String(item.total_rating_count),
      average_rating: String(item.average_rating),
    });
  };

  const saveEditManualRating = async (ratingId) => {
    try {
      const response = await apiFetch(`/api/manual-ratings/${ratingId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          brand: editManualRatingData.brand,
          platform: editManualRatingData.platform,
          sku_name: editManualRatingData.sku_name,
          total_rating_count: parseInt(editManualRatingData.total_rating_count),
          average_rating: parseFloat(editManualRatingData.average_rating),
        }),
      });
      if (response.ok) {
        toast.success('Rating updated');
        setEditingManualRating(null);
        await fetchManualRatings();
      } else {
        toast.error('Failed to update');
      }
    } catch (err) {
      toast.error('Failed to update');
    }
  };

  const handleManualBulkUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingManualBulk(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await apiFetch('/api/manual-ratings/bulk', {
        method: 'POST',
        body: formData,
      });
      if (response.ok) {
        const result = await response.json();
        toast.success(`${result.inserted} ratings uploaded${result.errors > 0 ? `, ${result.errors} errors` : ''}`);
        await fetchManualRatings();
      } else {
        const err = await response.json().catch(() => ({}));
        toast.error(err.detail || 'Upload failed');
      }
    } catch (err) {
      toast.error('Upload failed');
    } finally {
      setUploadingManualBulk(false);
      if (manualBulkFileRef.current) manualBulkFileRef.current.value = '';
    }
  };

  const downloadManualTemplate = async () => {
    try {
      const response = await apiFetch('/api/manual-ratings/template');
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'manual_ratings_template.csv';
        a.click();
        window.URL.revokeObjectURL(url);
      }
    } catch (err) {
      toast.error('Failed to download template');
    }
  };

  // Bulk upload for Tracked URLs
  const handleUrlBulkUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingUrlBulk(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await apiFetch('/api/scraper/urls/bulk', { method: 'POST', body: formData });
      if (response.ok) {
        const result = await response.json();
        toast.success(`${result.inserted} URLs uploaded${result.errors > 0 ? `, ${result.errors} errors` : ''}`);
        await fetchTrackedUrls();
      } else {
        const err = await response.json().catch(() => ({}));
        toast.error(err.detail || 'Upload failed');
      }
    } catch (err) { toast.error('Upload failed'); }
    finally { setUploadingUrlBulk(false); if (urlBulkFileRef.current) urlBulkFileRef.current.value = ''; }
  };

  const downloadUrlTemplate = () => {
    const csv = "Platform,Brand,URL\nAmazon,Wellcore,https://amazon.in/dp/B0EXAMPLE\nFlipkart,Wellcore,https://www.flipkart.com/product/p/itm?pid=EXAMPLE\n";
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'tracked_urls_template.csv'; a.click();
    window.URL.revokeObjectURL(url);
  };

  // Bulk upload for Tracked SKU URLs
  const handleSkuBulkUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingSkuBulk(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await apiFetch('/api/scraper/sku-urls/bulk', { method: 'POST', body: formData });
      if (response.ok) {
        const result = await response.json();
        toast.success(`${result.inserted} SKU URLs uploaded${result.errors > 0 ? `, ${result.errors} errors` : ''}`);
        await fetchSkuUrls();
        await fetchGroupedProducts();
      } else {
        const err = await response.json().catch(() => ({}));
        toast.error(err.detail || 'Upload failed');
      }
    } catch (err) { toast.error('Upload failed'); }
    finally { setUploadingSkuBulk(false); if (skuBulkFileRef.current) skuBulkFileRef.current.value = ''; }
  };

  const downloadSkuTemplate = () => {
    const csv = "Platform,Brand,SKU Name,URL,Image URL,Total Rating Count,Average Rating\nAmazon,Wellcore,Whey Protein 1kg,https://amazon.in/dp/B0EXAMPLE,https://example.com/image.jpg,,\nFlipkart,Wellcore,Creatine 250g,https://www.flipkart.com/product/p/itm?pid=EXAMPLE,,\nBlinkit,Wellcore,Whey Protein 1kg,,https://example.com/image.jpg,5420,4.3\nZepto,Wellcore,Creatine 250g,https://www.zepto.com/pn/product/pvid/EXAMPLE,,,\n";
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'tracked_sku_urls_template.csv'; a.click();
    window.URL.revokeObjectURL(url);
  };

  // Brand CRUD
  const fetchBrands = async () => {
    setLoadingBrands(true);
    try {
      const res = await apiFetch('/api/brands', { credentials: 'include' });
      if (res.ok) setBrands(await res.json());
    } catch (err) { console.error(err); }
    finally { setLoadingBrands(false); }
  };

  const saveBrand = async () => {
    if (!brandForm.name) { toast.error('Brand name is required'); return; }
    // Validate threshold if provided
    let threshold = null;
    if (brandForm.rating_threshold !== '' && brandForm.rating_threshold !== null && brandForm.rating_threshold !== undefined) {
      const t = parseFloat(brandForm.rating_threshold);
      if (Number.isNaN(t) || t < 1.0 || t > 5.0) {
        toast.error('Alert threshold must be a number between 1.0 and 5.0');
        return;
      }
      threshold = t;
    }
    const emailRaw = (brandForm.manager_email || '').trim();
    if (emailRaw) {
      const parts = emailRaw.replace(/;/g, ',').split(',').map(s => s.trim()).filter(Boolean);
      const bad = parts.find(p => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(p));
      if (bad) {
        toast.error(`'${bad}' is not a valid email`);
        return;
      }
    }
    // Validate channel stakeholder emails
    const cleanedChannelStakeholders = {};
    for (const [ch, raw] of Object.entries(brandForm.channel_stakeholders || {})) {
      const trimmed = (raw || '').trim();
      if (!trimmed) continue;
      const parts = trimmed.replace(/;/g, ',').split(',').map(s => s.trim()).filter(Boolean);
      const bad = parts.find(p => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(p));
      if (bad) {
        toast.error(`${ch}: '${bad}' is not a valid email`);
        return;
      }
      cleanedChannelStakeholders[ch] = trimmed;
    }
    const email = emailRaw;
    try {
      const isEdit = !!editingBrand;
      const url = isEdit ? `/api/brands/${editingBrand}` : '/api/brands';
      const method = isEdit ? 'PUT' : 'POST';
      const payload = {
        name: brandForm.name,
        logo_url: brandForm.logo_url || null,
        manager_email: email,                       // empty string clears on backend
        rating_threshold: threshold !== null ? threshold : 0,  // 0 clears on backend
        alert_frequency: (brandForm.alert_frequency || 'daily').toLowerCase(),
        alert_day_of_week: Number(brandForm.alert_day_of_week ?? 0),
        alert_hour_ist: Number(brandForm.alert_hour_ist ?? 9),
        channel_stakeholders: cleanedChannelStakeholders,
      };
      const res = await apiFetch(url, {
        method, headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        toast.success(isEdit ? 'Brand updated' : 'Brand added');
        setShowBrandDialog(false);
        setEditingBrand(null);
        setBrandForm({ name: '', logo_url: '', manager_email: '', rating_threshold: '', alert_frequency: 'daily', alert_day_of_week: 0, alert_hour_ist: 9, channel_stakeholders: {} });
        setBrandChannels([]);
        await fetchBrands();
      } else toast.error('Failed');
    } catch (err) { toast.error('Failed'); }
  };

  const deleteBrand = async (brandId) => {
    try {
      await apiFetch(`/api/brands/${brandId}`, { method: 'DELETE' });
      toast.success('Brand deleted');
      await fetchBrands();
    } catch (err) { toast.error('Failed'); }
  };

  const sendAlertNow = async (brand) => {
    if (!brand?.manager_email) {
      toast.error('Set a Brand Manager Email first to send alerts');
      return;
    }
    setSendingAlertFor(brand.brand_id);
    try {
      const res = await apiFetch(`/api/admin/alerts/check/${encodeURIComponent(brand.name)}`, { method: 'POST' });
      if (!res.ok) {
        if (res.status === 403) toast.error('Admin access required');
        else toast.error('Failed to send alert');
        return;
      }
      const data = await res.json();
      if (data.sent) {
        const totalEmails = data.total_emails || 1;
        const channels = Array.isArray(data.channels) ? data.channels.filter(c => c.sent) : [];
        const channelLabel = channels.length > 0
          ? channels.map(c => c.channel).join(', ')
          : (brand.manager_email || 'recipients');
        toast.success(
          `Sent ${totalEmails} alert email${totalEmails !== 1 ? 's' : ''} (${data.total_breaches} SKUs) — ${channelLabel}`,
          { duration: 6000 }
        );
      } else if (data.reason === 'no_breaches') {
        toast.info(`No alert sent — all SKUs are at or above the threshold of ${brand.rating_threshold ?? '4.3'}`);
      } else if (data.reason === 'no_manager') {
        toast.error('Brand Manager Email is not configured');
      } else if (data.reason === 'send_failed') {
        toast.error(`Email send failed: ${data.error || 'check Resend API key / domain'}`, { duration: 8000 });
      } else {
        toast.info('Alert check ran but no email was sent');
      }
    } catch (err) {
      toast.error('Failed to send alert');
    } finally {
      setSendingAlertFor(null);
    }
  };

  // Platform CRUD
  const fetchPlatforms = async () => {
    setLoadingPlatforms(true);
    try {
      const res = await apiFetch('/api/platforms', { credentials: 'include' });
      if (res.ok) setPlatforms(await res.json());
    } catch (err) { console.error(err); }
    finally { setLoadingPlatforms(false); }
  };

  const savePlatform = async () => {
    if (!platformForm.name) { toast.error('Platform name is required'); return; }
    try {
      const isEdit = !!editingPlatform;
      const url = isEdit ? `/api/platforms/${editingPlatform}` : '/api/platforms';
      const method = isEdit ? 'PUT' : 'POST';
      const res = await apiFetch(url, {
        method, headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: platformForm.name, icon_url: platformForm.icon_url || null }),
      });
      if (res.ok) {
        toast.success(isEdit ? 'Platform updated' : 'Platform added');
        setShowPlatformDialog(false);
        setEditingPlatform(null);
        setPlatformForm({ name: '', icon_url: '' });
        await fetchPlatforms();
      } else toast.error('Failed');
    } catch (err) { toast.error('Failed'); }
  };

  const deletePlatform = async (platformId) => {
    try {
      await apiFetch(`/api/platforms/${platformId}`, { method: 'DELETE' });
      toast.success('Platform deleted');
      await fetchPlatforms();
    } catch (err) { toast.error('Failed'); }
  };

  // Brand stats
  const fetchBrandStats = async () => {
    try {
      const res = await apiFetch('/api/brands/stats', { credentials: 'include' });
      if (res.ok) setBrandStats(await res.json());
    } catch (err) { console.error(err); }
  };

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  }, []);

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  const handleFile = async (file) => {
    const validTypes = [
      'text/csv',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    ];

    if (!validTypes.includes(file.type) && !file.name.match(/\.(csv|xlsx|xls)$/i)) {
      toast.error('Please upload a CSV or Excel file');
      return;
    }

    if (!selectedPlatform) {
      toast.error('Please select a platform before uploading');
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setUploadResult(null);

    const formData = new FormData();
    formData.append('file', file);

    try {
      // Simulate progress
      const progressInterval = setInterval(() => {
        setUploadProgress((prev) => Math.min(prev + 10, 90));
      }, 100);

      const response = await apiFetch(`/api/reviews/upload?platform=${encodeURIComponent(selectedPlatform)}`, {
        method: 'POST',
        body: formData,
      });

      clearInterval(progressInterval);
      setUploadProgress(100);

      if (response.ok) {
        const data = await response.json();
        setUploadResult({ success: true, ...data });
        toast.success(`Uploaded ${data.count} reviews successfully`);
      } else {
        const error = await response.json();
        setUploadResult({ success: false, error: error.detail });
        toast.error(error.detail || 'Upload failed');
      }
    } catch (error) {
      console.error('Upload error:', error);
      setUploadResult({ success: false, error: 'Upload failed' });
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const analyzeReviews = async () => {
    setAnalyzing(true);
    let totalAnalyzed = 0;
    let remaining = 1;
    
    try {
      // Keep analyzing in batches until all done
      while (remaining > 0) {
        const response = await apiFetch('/api/reviews/analyze?batch_size=500', {
          method: 'POST',
        });

        if (response.ok) {
          const data = await response.json();
          totalAnalyzed += data.count;
          remaining = data.total_remaining || 0;
          
          if (data.count > 0) {
            toast.info(`Analyzed ${totalAnalyzed} reviews... ${remaining > 0 ? `${remaining} remaining` : 'Complete!'}`);
          }
          
          if (remaining === 0 || data.count === 0) {
            break;
          }
        } else {
          toast.error('Analysis failed');
          break;
        }
      }
      
      if (totalAnalyzed > 0) {
        toast.success(`Successfully analyzed ${totalAnalyzed} reviews with AI`);
      } else {
        toast.info('No unanalyzed reviews found');
      }
    } catch (error) {
      console.error('Analysis error:', error);
      toast.error('Analysis failed');
    } finally {
      setAnalyzing(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="settings-page">
      {/* Page header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold font-heading tracking-tight">Control Panel</h1>
          <p className="text-muted-foreground mt-1">
            Manage products, brands, and platform configurations
          </p>
        </div>
        <div className="flex items-center gap-2 mt-1" data-testid="proxy-status">
          <div className="flex items-center gap-1.5 border border-[#2eb4ac]/30 rounded-full px-3 py-1 bg-[#2eb4ac]/5">
            <div className="w-1.5 h-1.5 rounded-full bg-[#2eb4ac] animate-pulse" />
            <span className="text-[10px] font-medium text-[#2eb4ac]">Webshare Proxy Active</span>
          </div>
        </div>
      </div>

      <Tabs defaultValue={new URLSearchParams(window.location.search).get('tab') || 'tracking'} className="space-y-6">
        <TabsList>
          <TabsTrigger value="tracking" data-testid="tab-tracking">
            <Link className="w-4 h-4 mr-2" />
            Products
          </TabsTrigger>
          <TabsTrigger value="brands" data-testid="tab-brands">
            <Package className="w-4 h-4 mr-2" />
            Brands
          </TabsTrigger>
          <TabsTrigger value="platforms" data-testid="tab-platforms">
            <Store className="w-4 h-4 mr-2" />
            Platforms
          </TabsTrigger>
        </TabsList>

        <TabsContent value="tracking" className="space-y-6">
          {/* Bulk Import Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card className="card-hover">
              <CardContent className="pt-5 pb-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-sm font-semibold flex items-center gap-2"><Upload className="w-4 h-4 text-[#8b5cf6]" /> Bulk Import Products</h3>
                    <p className="text-xs text-muted-foreground mt-1">Upload a CSV to add multiple SKU product URLs at once</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="ghost" size="sm" onClick={downloadSkuTemplate} className="gap-1 text-[#8b5cf6] text-xs" data-testid="bulk-product-template">
                      <Download className="w-3 h-3" />Template
                    </Button>
                    <input ref={skuBulkFileRef} type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={handleSkuBulkUpload} />
                    <Button variant="outline" size="sm" onClick={() => skuBulkFileRef.current?.click()} disabled={uploadingSkuBulk} className="gap-1 text-xs" data-testid="bulk-product-upload">
                      {uploadingSkuBulk ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
                      Upload
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="card-hover">
              <CardContent className="pt-5 pb-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-sm font-semibold flex items-center gap-2"><Globe className="w-4 h-4 text-[#2eb4ac]" /> Global URLs</h3>
                    <p className="text-xs text-muted-foreground mt-1">Upload a CSV to add brand-level tracking URLs</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="ghost" size="sm" onClick={downloadUrlTemplate} className="gap-1 text-[#2eb4ac] text-xs" data-testid="bulk-url-template">
                      <Download className="w-3 h-3" />Template
                    </Button>
                    <input ref={urlBulkFileRef} type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={handleUrlBulkUpload} />
                    <Button variant="outline" size="sm" onClick={() => urlBulkFileRef.current?.click()} disabled={uploadingUrlBulk} className="gap-1 text-xs" data-testid="bulk-url-upload">
                      {uploadingUrlBulk ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
                      Upload
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Global URLs Card */}
          <Card className="card-hover" data-testid="global-urls-card">
            <div
              role="button"
              tabIndex={0}
              onClick={() => setGlobalUrlsExpanded(!globalUrlsExpanded)}
              className="flex items-center justify-between px-4 py-2 cursor-pointer hover:bg-accent/30 transition-colors"
            >
              <div className="flex items-center gap-2">
                <ChevronDown className={`w-3.5 h-3.5 text-muted-foreground transition-transform ${globalUrlsExpanded ? '' : '-rotate-90'}`} />
                <Globe className="w-3.5 h-3.5 text-[#2eb4ac]" />
                <span className="text-xs font-semibold">Global URLs</span>
                <Badge variant="secondary" className="text-[10px] px-1.5 py-0">{trackedUrls.length}</Badge>
              </div>
              <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); fetchTrackedUrls(); }} disabled={loadingUrls} className="gap-1 h-6 px-2 text-[10px]">
                <RefreshCw className={`w-2.5 h-2.5 ${loadingUrls ? 'animate-spin' : ''}`} />Reload
              </Button>
            </div>
            {globalUrlsExpanded && (
              <div className="px-4 pb-3">
                {trackedUrls.length === 0 ? (
                  <p className="text-[10px] text-muted-foreground text-center py-2">No global URLs tracked.</p>
                ) : (
                  <div className="rounded border border-border overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow className="h-7">
                          <TableHead className="text-[10px] py-1">Platform</TableHead>
                          <TableHead className="text-[10px] py-1">Brand</TableHead>
                          <TableHead className="text-[10px] py-1">URL</TableHead>
                          <TableHead className="text-[10px] py-1 text-center">Rating</TableHead>
                          <TableHead className="text-[10px] py-1 text-center">Reviews</TableHead>
                          <TableHead className="text-[10px] py-1 w-8"></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {trackedUrls.map((item) => (
                          <TableRow key={item.url_id} className="h-8">
                            <TableCell className="py-1"><Badge variant="outline" className="text-[9px] px-1.5 py-0">{item.platform}</Badge></TableCell>
                            <TableCell className="text-[10px] font-medium py-1">{item.brand}</TableCell>
                            <TableCell className="py-1">
                              <a href={item.url} target="_blank" rel="noopener noreferrer" className="text-[10px] text-[#2eb4ac] hover:underline flex items-center gap-0.5 max-w-[180px] truncate">
                                {item.url?.replace(/https?:\/\/(www\.)?/, '').slice(0, 35)}
                                <ExternalLink className="w-2 h-2 shrink-0" />
                              </a>
                            </TableCell>
                            <TableCell className="text-center py-1 text-[10px]">
                              {item.last_rating ? (
                                <span className="flex items-center justify-center gap-0.5"><Star className="w-2.5 h-2.5 text-[#f59e0b] fill-[#f59e0b]" />{item.last_rating}</span>
                              ) : '-'}
                            </TableCell>
                            <TableCell className="text-center py-1 text-[10px]">{item.last_review_count?.toLocaleString() || '-'}</TableCell>
                            <TableCell className="py-1">
                              <Button variant="ghost" size="sm" onClick={() => deleteTrackingUrl(item.url_id)} className="h-5 w-5 p-0 text-destructive hover:text-destructive">
                                <Trash2 className="w-2.5 h-2.5" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>
            )}
          </Card>

          {/* Products by Brand */}
          <Card className="card-hover" data-testid="products-by-brand-card">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="font-heading text-base">Products by Brand</CardTitle>
                  <CardDescription>
                    {groupedProducts.total_products || 0} products across {groupedProducts.total_brands || 0} brands
                  </CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-1.5 border border-border rounded-md px-2 py-1 mr-1" data-testid="auto-fetch-card">
                    <Timer className="w-3.5 h-3.5 text-[#2eb4ac]" />
                    <Select value={String(autoFetchInterval)} onValueChange={(v) => updateAutoFetchInterval(Number(v))} disabled={savingAutoFetch}>
                      <SelectTrigger className="h-7 w-[130px] border-0 bg-transparent px-1 text-xs" data-testid="auto-fetch-select">
                        <SelectValue placeholder="Auto-Fetch" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="0">Off (Manual)</SelectItem>
                        <SelectItem value="1">Every 1 Hour</SelectItem>
                        <SelectItem value="6">Every 6 Hours</SelectItem>
                        <SelectItem value="12">Every 12 Hours</SelectItem>
                        <SelectItem value="24">Every 24 Hours</SelectItem>
                      </SelectContent>
                    </Select>
                    {autoFetchInterval > 0 ? (
                      <Badge className="bg-[#2eb4ac]/10 text-[#2eb4ac] border-[#2eb4ac]/30 text-[10px] px-1.5 py-0" variant="outline">Active</Badge>
                    ) : (
                      <Badge variant="outline" className="text-muted-foreground text-[10px] px-1.5 py-0">Off</Badge>
                    )}
                  </div>
                  <Button variant="outline" size="sm" onClick={fetchGroupedProducts} disabled={loadingGroupedProducts} className="gap-1" data-testid="reload-products-btn">
                    <RefreshCw className={`w-3 h-3 ${loadingGroupedProducts ? 'animate-spin' : ''}`} />
                    Reload
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => { const all = {}; (groupedProducts.brands || []).forEach(b => all[b.brand] = true); setExpandedBrands(all); }} className="text-xs gap-1">
                    <ChevronDown className="w-3 h-3" />Expand All
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => setExpandedBrands({})} className="text-xs gap-1">
                    <ChevronDown className="w-3 h-3 rotate-90" />Collapse All
                  </Button>
                  <Button size="sm" onClick={() => { setEditProduct({ brand: '', sku_name: '', platform_urls: {}, platform_ratings: {}, image_url: '' }); setShowProductDialog(true); }} className="bg-[#8b5cf6] hover:bg-[#8b5cf6]/90 gap-1" data-testid="add-product-btn">
                    <Plus className="w-3.5 h-3.5" />Add Product
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {(groupedProducts.brands || []).length === 0 ? (
                <div className="text-center py-10 text-muted-foreground">
                  <Package className="w-10 h-10 mx-auto mb-3 opacity-30" />
                  <p className="text-sm">No products added yet. Use bulk import or add products manually.</p>
                </div>
              ) : (
                <div className="space-y-1">
                  {(groupedProducts.brands || []).map((brandGroup) => (
                    <div key={brandGroup.brand} className="border border-border rounded-lg overflow-hidden">
                      <div
                        role="button"
                        tabIndex={0}
                        onClick={() => setExpandedBrands(prev => ({ ...prev, [brandGroup.brand]: !prev[brandGroup.brand] }))}
                        className="w-full flex items-center justify-between px-4 py-3 hover:bg-accent/40 transition-colors text-left cursor-pointer"
                        data-testid={`brand-group-${brandGroup.brand}`}
                      >
                        <div className="flex items-center gap-3">
                          <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform ${expandedBrands[brandGroup.brand] ? '' : '-rotate-90'}`} />
                          <span className="font-semibold text-sm">{brandGroup.brand}</span>
                          <Badge variant="secondary" className="text-xs">{brandGroup.product_count} products</Badge>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 px-2"
                          onClick={(e) => { e.stopPropagation(); setEditProduct({ brand: brandGroup.brand, sku_name: '', platform_urls: {}, platform_ratings: {}, image_url: '' }); setShowProductDialog(true); }}
                        >
                          <Plus className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                      {expandedBrands[brandGroup.brand] && (
                        <div className="border-t border-border">
                          {brandGroup.products.map((product) => (
                            <div key={product.sku_name} className="flex items-center justify-between px-4 py-2.5 hover:bg-accent/20 transition-colors border-b border-border/50 last:border-0">
                              <div className="flex items-center gap-3 min-w-0 flex-1">
                                <div className="w-8 h-8 rounded bg-accent/50 shrink-0 overflow-hidden flex items-center justify-center">
                                  {product.image_url ? (
                                    <img src={product.image_url} alt="" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                                  ) : (
                                    <Package className="w-3.5 h-3.5 text-muted-foreground" />
                                  )}
                                </div>
                                <span className="text-sm truncate">{product.sku_name}</span>
                                <div className="flex items-center gap-1.5 ml-2">
                                  <TooltipProvider>
                                  {product.platforms.map((p) => (
                                    <Tooltip key={p.url_id || `${p.platform}-${product.sku_name}`}>
                                      <TooltipTrigger asChild>
                                        <span className={`inline-flex items-center justify-center w-5 h-5 rounded text-[9px] font-bold ${
                                          p.platform === 'Amazon' ? 'bg-[#ff9900]/15 text-[#ff9900]' :
                                          p.platform === 'Flipkart' ? 'bg-[#2874f0]/15 text-[#2874f0]' :
                                          p.platform === 'Blinkit' ? 'bg-[#f7c948]/15 text-[#c4960a]' :
                                          p.platform === 'Zepto' ? 'bg-[#8b2252]/15 text-[#8b2252]' :
                                          'bg-accent text-muted-foreground'
                                        }`}>
                                          {p.platform.charAt(0)}
                                        </span>
                                      </TooltipTrigger>
                                      <TooltipContent side="top" className="text-xs">
                                        {p.platform}
                                        {p.last_rating ? ` — ${p.last_rating} stars` : ''}
                                        {p.total_rating_count ? ` — ${p.total_rating_count.toLocaleString()} ratings, ${p.average_rating} avg` : ''}
                                      </TooltipContent>
                                    </Tooltip>
                                  ))}
                                  </TooltipProvider>
                                </div>
                              </div>
                              <div className="flex items-center gap-1 shrink-0">
                                <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => {
                                  const urls = {};
                                  const ratings = {};
                                  product.platforms.forEach(p => {
                                    urls[p.platform] = p.url;
                                    if (p.total_rating_count !== undefined && p.total_rating_count !== null) {
                                      ratings[p.platform] = {
                                        total_rating_count: String(p.total_rating_count),
                                        average_rating: String(p.average_rating || ''),
                                      };
                                    }
                                  });
                                  setEditProduct({ brand: brandGroup.brand, sku_name: product.sku_name, original_sku_name: product.sku_name, platform_urls: urls, platform_ratings: ratings, image_url: product.image_url || '', isEdit: true });
                                  setShowProductDialog(true);
                                }} data-testid={`edit-product-${product.sku_name}`}>
                                  <Pencil className="w-3 h-3" />
                                </Button>
                                <Button variant="ghost" size="sm" className="h-7 px-2 text-destructive hover:text-destructive" onClick={async () => {
                                  try {
                                    await apiFetch(`/api/products/${encodeURIComponent(brandGroup.brand)}/${encodeURIComponent(product.sku_name)}`, { method: 'DELETE' });
                                    toast.success('Product removed');
                                    fetchGroupedProducts();
                                  } catch (err) { toast.error('Failed to delete product'); }
                                }} data-testid={`delete-product-${product.sku_name}`}>
                                  <Trash2 className="w-3 h-3" />
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Meta Comments Tracking */}
          <Card className="card-hover" data-testid="meta-comments-card">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <CardTitle className="font-heading text-base flex items-center gap-2">
                    <svg className="w-4 h-4 text-[#1877f2]" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.477 2 2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.879V14.89h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.989C18.343 21.129 22 16.99 22 12c0-5.523-4.477-10-10-10z"/></svg>
                    Meta Comments
                  </CardTitle>
                  <CardDescription>Scrape comments + post stats (views, likes) from Facebook/Instagram via Apify</CardDescription>
                </div>
                <div className="flex items-center gap-1.5 border border-border rounded-md px-2 py-1" data-testid="meta-auto-fetch-card">
                  <Timer className="w-3.5 h-3.5 text-[#1877f2]" />
                  <Select value={String(metaAutoFetchInterval)} onValueChange={(v) => updateMetaAutoFetch(Number(v))} disabled={savingMetaAutoFetch}>
                    <SelectTrigger className="h-7 w-[150px] border-0 bg-transparent px-1 text-xs" data-testid="meta-auto-fetch-select">
                      <SelectValue placeholder="Auto-refresh stats" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0">Off (Manual)</SelectItem>
                      <SelectItem value="1">Every 1 Hour</SelectItem>
                      <SelectItem value="2">Every 2 Hours</SelectItem>
                      <SelectItem value="6">Every 6 Hours</SelectItem>
                      <SelectItem value="12">Every 12 Hours</SelectItem>
                      <SelectItem value="24">Every 24 Hours</SelectItem>
                    </SelectContent>
                  </Select>
                  {metaAutoFetchInterval > 0 ? (
                    <Badge className="bg-[#1877f2]/10 text-[#1877f2] border-[#1877f2]/30 text-[10px] px-1.5 py-0" variant="outline">Active</Badge>
                  ) : (
                    <Badge variant="outline" className="text-muted-foreground text-[10px] px-1.5 py-0">Off</Badge>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Add new post */}
              <div className="flex items-end gap-2">
                <div className="w-32">
                  <label className="text-xs font-medium mb-1 block">Brand</label>
                  <Select value={metaPostBrand} onValueChange={setMetaPostBrand}>
                    <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Brand" /></SelectTrigger>
                    <SelectContent>
                      {brands.map(b => <SelectItem key={b.name} value={b.name}>{b.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex-1">
                  <label className="text-xs font-medium mb-1 block">Ad/Post URL</label>
                  <Input placeholder="https://www.facebook.com/page/posts/123... or Instagram post URL" value={metaPostUrl} onChange={(e) => setMetaPostUrl(e.target.value)} className="h-8 text-xs" data-testid="meta-post-url-input" />
                </div>
                <div className="w-40">
                  <label className="text-xs font-medium mb-1 block">Label (optional)</label>
                  <Input placeholder="Ad name / campaign" value={metaPostLabel} onChange={(e) => setMetaPostLabel(e.target.value)} className="h-8 text-xs" />
                </div>
                <Button size="sm" onClick={addMetaPost} disabled={addingMetaPost} className="h-8 bg-[#1877f2] hover:bg-[#1877f2]/90 gap-1" data-testid="add-meta-post-btn">
                  <Plus className="w-3 h-3" />Add
                </Button>
              </div>

              {/* Posts list */}
              {metaPosts.length > 0 && (
                <div className="rounded border border-border overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="h-8">
                        <TableHead className="text-[10px] py-1">Brand</TableHead>
                        <TableHead className="text-[10px] py-1">URL / Label</TableHead>
                        <TableHead className="text-[10px] py-1 text-center">Comments</TableHead>
                        <TableHead className="text-[10px] py-1 text-center">Status</TableHead>
                        <TableHead className="text-[10px] py-1 w-24 text-center">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {metaPosts.map((post) => (
                        <TableRow key={post.post_id} className="h-9">
                          <TableCell className="text-xs font-medium py-1">{post.brand}</TableCell>
                          <TableCell className="py-1">
                            <div className="flex flex-col">
                              {post.label && <span className="text-xs font-medium">{post.label}</span>}
                              <a href={post.url} target="_blank" rel="noopener noreferrer" className="text-[10px] text-[#1877f2] hover:underline truncate max-w-[250px]">
                                {post.url?.replace('https://www.facebook.com/', '').slice(0, 50)}
                                <ExternalLink className="w-2 h-2 inline ml-0.5" />
                              </a>
                            </div>
                          </TableCell>
                          <TableCell className="text-center text-xs py-1">{post.comments_fetched || 0}</TableCell>
                          <TableCell className="text-center py-1">
                            <Badge variant="outline" className={`text-[9px] ${post.status === 'success' ? 'text-[#2eb4ac] border-[#2eb4ac]/30' : post.status === 'error' ? 'text-destructive border-destructive/30' : 'text-muted-foreground'}`}>
                              {post.status}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-center py-1">
                            <div className="flex items-center justify-center gap-1">
                              <Button variant="ghost" size="sm" onClick={() => fetchMetaComments(post.post_id)} disabled={fetchingMeta[post.post_id]} className="h-6 px-2 text-[10px] gap-1 text-[#1877f2]" data-testid={`fetch-meta-${post.post_id}`}>
                                <RefreshCw className={`w-2.5 h-2.5 ${fetchingMeta[post.post_id] ? 'animate-spin' : ''}`} />
                                Fetch
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => deleteMetaPost(post.post_id)} className="h-6 px-1.5 text-destructive hover:text-destructive">
                                <Trash2 className="w-2.5 h-2.5" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
              {metaPosts.length === 0 && (
                <p className="text-xs text-muted-foreground text-center py-3">No Meta posts tracked. Add an ad URL above to start scraping comments.</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>


        <TabsContent value="brands" className="space-y-6">
          <Card className="card-hover" data-testid="brands-card">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="font-heading">Managed Brands</CardTitle>
                  <CardDescription>Brands tracked in your review intelligence platform</CardDescription>
                </div>
                <Button size="sm" className="bg-[#8b5cf6] hover:bg-[#8b5cf6]/90 gap-1" onClick={() => { setEditingBrand(null); setBrandForm({ name: '', logo_url: '', manager_email: '', rating_threshold: '', alert_frequency: 'daily', alert_day_of_week: 0, alert_hour_ist: 9, channel_stakeholders: {} }); setBrandChannels([]); setShowBrandDialog(true); }} data-testid="add-brand-btn">
                  <Plus className="w-3 h-3" /> Add Brand
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {loadingBrands ? (
                <div className="text-center py-8 text-muted-foreground"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {brands.map((brand) => (
                    <div key={brand.brand_id || brand.name} className="p-4 rounded-xl border border-border hover:border-[#8b5cf6]/50 transition-colors group">
                      <div className="flex items-center gap-3 mb-2">
                        {brand.logo_url ? (
                          <img src={brand.logo_url} alt={brand.name} className="w-10 h-10 rounded-lg object-cover bg-accent" />
                        ) : (
                          <div className="w-10 h-10 rounded-lg bg-[#8b5cf6]/10 flex items-center justify-center">
                            <Package className="w-5 h-5 text-[#8b5cf6]" />
                          </div>
                        )}
                        <div className="flex-1">
                          <p className="font-semibold">{brand.name}</p>
                          {brandStats[brand.name] && (
                            <p className="text-xs text-muted-foreground">{brandStats[brand.name].reduce((s, p) => s + p.total_reviews, 0).toLocaleString()} uploaded reviews</p>
                          )}
                        </div>
                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <Button variant="ghost" size="sm" className="h-7 px-2" onClick={async () => {
                            setEditingBrand(brand.brand_id);
                            setBrandForm({
                              name: brand.name,
                              logo_url: brand.logo_url || '',
                              manager_email: brand.manager_email || '',
                              rating_threshold: brand.rating_threshold != null ? String(brand.rating_threshold) : '',
                              alert_frequency: (brand.alert_frequency || 'daily'),
                              alert_day_of_week: brand.alert_day_of_week != null ? brand.alert_day_of_week : 0,
                              alert_hour_ist: brand.alert_hour_ist != null ? brand.alert_hour_ist : 9,
                              channel_stakeholders: brand.channel_stakeholders || {},
                            });
                            setBrandChannels([]);
                            setShowBrandDialog(true);
                            try {
                              const res = await apiFetch(`/api/brands/${encodeURIComponent(brand.name)}/channels`);
                              if (res.ok) {
                                const data = await res.json();
                                setBrandChannels(Array.isArray(data.channels) ? data.channels : []);
                              }
                            } catch (_) { /* non-fatal */ }
                          }} data-testid={`edit-brand-${brand.brand_id}`}>
                            <Pencil className="w-3 h-3" />
                          </Button>
                          <Button variant="ghost" size="sm" className="h-7 px-2 text-destructive" onClick={() => deleteBrand(brand.brand_id)} data-testid={`delete-brand-${brand.brand_id}`}>
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                      {brandStats[brand.name] && (
                        <div className="flex gap-2 flex-wrap mt-2">
                          {brandStats[brand.name].map((ps) => (
                            <Badge key={ps.platform} variant="secondary" className="text-xs gap-1">
                              <Star className="w-3 h-3 text-[#f59e0b] fill-[#f59e0b]" />{ps.avg_rating} {ps.platform}
                            </Badge>
                          ))}
                        </div>
                      )}
                      {(brand.manager_email || brand.rating_threshold != null) && (
                        <div className="mt-3 pt-3 border-t border-border/60 space-y-2" data-testid={`brand-alert-config-${brand.brand_id}`}>
                          <div className="flex items-center justify-between gap-2 text-[11px]">
                            <div className="flex items-center gap-1.5 text-muted-foreground truncate min-w-0 flex-1">
                              <Bell className="w-3 h-3 text-[#2eb4ac] shrink-0" />
                              {(() => {
                                const emails = (brand.manager_email || '').split(',').map(s => s.trim()).filter(Boolean);
                                if (emails.length === 0) return <span className="italic">No manager email</span>;
                                if (emails.length === 1) return <span className="truncate" title={emails[0]}>{emails[0]}</span>;
                                return (
                                  <span className="truncate" title={emails.join(', ')}>
                                    {emails[0]} <span className="text-[#94a3b8]">+{emails.length - 1} more</span>
                                  </span>
                                );
                              })()}
                            </div>
                            <Badge variant="outline" className="text-[10px] h-5 px-1.5 gap-1 shrink-0">
                              <Star className="w-2.5 h-2.5 text-[#f59e0b] fill-[#f59e0b]" />
                              &lt; {brand.rating_threshold != null ? brand.rating_threshold : '4.3'}
                            </Badge>
                            {isAdmin && brand.manager_email && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-6 px-2 text-[10px] gap-1 border-[#2eb4ac]/40 text-[#2eb4ac] hover:bg-[#2eb4ac]/10 hover:text-[#2eb4ac] shrink-0"
                                onClick={() => sendAlertNow(brand)}
                                disabled={sendingAlertFor === brand.brand_id}
                                data-testid={`send-alert-now-${brand.brand_id}`}
                                title="Run an immediate threshold check and email the manager if any SKU is below threshold"
                              >
                                {sendingAlertFor === brand.brand_id ? (
                                  <Loader2 className="w-3 h-3 animate-spin" />
                                ) : (
                                  <Send className="w-3 h-3" />
                                )}
                                Send Now
                              </Button>
                            )}
                          </div>
                          <div className="flex items-center justify-between gap-2 text-[10px] text-muted-foreground" data-testid={`brand-alert-meta-${brand.brand_id}`}>
                            <span className="flex items-center gap-1 truncate">
                              <Calendar className="w-3 h-3 shrink-0 text-[#94a3b8]" />
                              {(() => {
                                const freq = brand.alert_frequency || 'daily';
                                const hr = brand.alert_hour_ist != null ? brand.alert_hour_ist : 9;
                                const ampm = hr < 12 ? 'AM' : 'PM';
                                const hr12 = ((hr + 11) % 12) + 1;
                                const dow = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'][brand.alert_day_of_week ?? 0];
                                return freq === 'weekly'
                                  ? `Weekly · ${dow} ${hr12}:00 ${ampm} IST`
                                  : `Daily · ${hr12}:00 ${ampm} IST`;
                              })()}
                            </span>
                            <span className="truncate text-right" title={brand.last_email_at || ''}>
                              {brand.last_email_at ? (
                                <span className="flex items-center gap-1 justify-end">
                                  <CheckCircle2 className="w-3 h-3 text-[#10b981]" />
                                  Last sent: {(() => {
                                    const diffMs = Date.now() - new Date(brand.last_email_at).getTime();
                                    const mins = Math.floor(diffMs / 60000);
                                    if (mins < 1) return 'just now';
                                    if (mins < 60) return `${mins}m ago`;
                                    const hrs = Math.floor(mins / 60);
                                    if (hrs < 24) return `${hrs}h ago`;
                                    const days = Math.floor(hrs / 24);
                                    return `${days}d ago`;
                                  })()}
                                </span>
                              ) : brand.last_email_error ? (
                                <span className="flex items-center gap-1 justify-end text-[#ef4444]" title={brand.last_email_error}>
                                  <AlertCircle className="w-3 h-3" />
                                  Last attempt failed
                                </span>
                              ) : (
                                <span className="italic">Never sent</span>
                              )}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <BrandAliasesCard onMerged={() => { fetchBrands(); fetchGroupedProducts && fetchGroupedProducts(); }} />
        </TabsContent>

        <TabsContent value="platforms" className="space-y-6">
          <Card className="card-hover" data-testid="platforms-card">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="font-heading">Connected Platforms</CardTitle>
                  <CardDescription>Marketplaces configured for review aggregation</CardDescription>
                </div>
                <Button size="sm" className="bg-[#8b5cf6] hover:bg-[#8b5cf6]/90 gap-1" onClick={() => { setEditingPlatform(null); setPlatformForm({ name: '', icon_url: '' }); setShowPlatformDialog(true); }} data-testid="add-platform-btn">
                  <Plus className="w-3 h-3" /> Add Platform
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {loadingPlatforms ? (
                <div className="text-center py-8 text-muted-foreground"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {platforms.map((platform) => (
                    <div key={platform.platform_id || platform.name} className="p-4 rounded-xl border border-border hover:border-[#2eb4ac]/50 transition-colors group">
                      <div className="flex items-center gap-3">
                        {platform.icon_url ? (
                          <img src={platform.icon_url} alt={platform.name} className="w-10 h-10 rounded-lg object-cover bg-accent" />
                        ) : (
                          <div className="w-10 h-10 rounded-lg bg-[#2eb4ac]/10 flex items-center justify-center">
                            <Store className="w-5 h-5 text-[#2eb4ac]" />
                          </div>
                        )}
                        <div className="flex-1">
                          <p className="font-semibold">{platform.name}</p>
                        </div>
                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => { setEditingPlatform(platform.platform_id); setPlatformForm({ name: platform.name, icon_url: platform.icon_url || '' }); setShowPlatformDialog(true); }} data-testid={`edit-platform-${platform.platform_id}`}>
                            <Pencil className="w-3 h-3" />
                          </Button>
                          <Button variant="ghost" size="sm" className="h-7 px-2 text-destructive" onClick={() => deletePlatform(platform.platform_id)} data-testid={`delete-platform-${platform.platform_id}`}>
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Product Edit Dialog */}
      <Dialog open={showProductDialog} onOpenChange={setShowProductDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{editProduct.isEdit ? 'Edit Product' : 'Add Product'}</DialogTitle>
            <DialogDescription>
              {editProduct.isEdit ? 'Update platform URLs for this product' : 'Add a new product with platform-specific URLs'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <label className="text-sm font-medium">Brand</label>
                <Select value={editProduct.brand} onValueChange={(v) => setEditProduct({ ...editProduct, brand: v })} disabled={editProduct.isEdit}>
                  <SelectTrigger data-testid="product-dialog-brand">
                    <SelectValue placeholder="Select brand" />
                  </SelectTrigger>
                  <SelectContent>
                    {brands.map((b) => (
                      <SelectItem key={b.brand_id || b.name} value={b.name}>{b.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <label className="text-sm font-medium">Product / SKU Name</label>
                <Input
                  placeholder="e.g., Whey Protein 1kg"
                  value={editProduct.sku_name}
                  onChange={(e) => setEditProduct({ ...editProduct, sku_name: e.target.value })}
                  data-testid="product-dialog-sku"
                />
                {editProduct.isEdit && editProduct.original_sku_name && editProduct.sku_name !== editProduct.original_sku_name && (
                  <p className="text-[11px] text-[#f59e0b]" data-testid="product-dialog-rename-hint">
                    Renaming from &quot;{editProduct.original_sku_name}&quot; — reviews, tracked URLs, ratings and hidden-SKU flags will be updated.
                  </p>
                )}
              </div>
            </div>
            <div className="space-y-3">
              <label className="text-sm font-medium">Platform Configuration</label>
              {platforms.map((p) => (
                <div key={p.name} className="space-y-1.5">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className={`w-24 justify-center text-xs shrink-0 ${MANUAL_PLATFORMS.includes(p.name) ? 'border-[#f59e0b]/50 text-[#f59e0b]' : ''}`}>{p.name}</Badge>
                    <Input
                      placeholder={MANUAL_PLATFORMS.includes(p.name) ? `${p.name} URL (optional)` : `${p.name} URL (leave empty to skip)`}
                      value={editProduct.platform_urls[p.name] || ''}
                      onChange={(e) => setEditProduct({
                        ...editProduct,
                        platform_urls: { ...editProduct.platform_urls, [p.name]: e.target.value },
                      })}
                      className="text-xs"
                      data-testid={`product-dialog-url-${p.name}`}
                    />
                  </div>
                  {MANUAL_PLATFORMS.includes(p.name) && (
                    <div className="flex items-center gap-2 ml-[6.5rem]">
                      <Input
                        type="number"
                        placeholder="Total Ratings"
                        value={editProduct.platform_ratings[p.name]?.total_rating_count || ''}
                        onChange={(e) => setEditProduct({
                          ...editProduct,
                          platform_ratings: {
                            ...editProduct.platform_ratings,
                            [p.name]: { ...(editProduct.platform_ratings[p.name] || {}), total_rating_count: e.target.value },
                          },
                        })}
                        className="text-xs w-32"
                        data-testid={`product-dialog-rating-count-${p.name}`}
                      />
                      <Input
                        type="number"
                        step="0.1"
                        min="0"
                        max="5"
                        placeholder="Avg Rating"
                        value={editProduct.platform_ratings[p.name]?.average_rating || ''}
                        onChange={(e) => setEditProduct({
                          ...editProduct,
                          platform_ratings: {
                            ...editProduct.platform_ratings,
                            [p.name]: { ...(editProduct.platform_ratings[p.name] || {}), average_rating: e.target.value },
                          },
                        })}
                        className="text-xs w-28"
                        data-testid={`product-dialog-avg-rating-${p.name}`}
                      />
                      <span className="text-[10px] text-muted-foreground whitespace-nowrap">Rating Count &amp; Avg</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div className="space-y-2 pt-3 border-t border-border">
              <label className="text-sm font-medium flex items-center gap-1.5">
                <ImageIcon className="w-4 h-4" />
                Product Image
              </label>
              <p className="text-xs text-muted-foreground">Paste a direct image URL (right-click product image → Copy image address)</p>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-lg border border-border bg-accent/30 shrink-0 overflow-hidden flex items-center justify-center">
                  {editProduct.image_url ? (
                    <img src={editProduct.image_url} alt="Preview" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                  ) : (
                    <ImageIcon className="w-5 h-5 text-muted-foreground/40" />
                  )}
                </div>
                <Input
                  placeholder="https://example.com/product-image.jpg"
                  value={editProduct.image_url || ''}
                  onChange={(e) => setEditProduct({ ...editProduct, image_url: e.target.value })}
                  className="text-xs flex-1"
                  data-testid="product-dialog-image-url"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowProductDialog(false)}>Cancel</Button>
            <Button onClick={saveProduct} disabled={savingProduct} className="bg-[#8b5cf6] hover:bg-[#8b5cf6]/90" data-testid="save-product-btn">
              {savingProduct ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {editProduct.isEdit ? 'Update' : 'Add Product'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Brand Dialog */}
      <Dialog open={showBrandDialog} onOpenChange={setShowBrandDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingBrand ? 'Edit Brand' : 'Add Brand'}</DialogTitle>
            <DialogDescription>Enter brand details</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Brand Name</label>
              <Input placeholder="e.g., Wellcore" value={brandForm.name} onChange={(e) => setBrandForm({ ...brandForm, name: e.target.value })} data-testid="brand-name-input" />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium flex items-center gap-1"><Image className="w-4 h-4" /> Logo URL</label>
              <Input placeholder="https://example.com/logo.png" value={brandForm.logo_url} onChange={(e) => setBrandForm({ ...brandForm, logo_url: e.target.value })} data-testid="brand-logo-input" />
              {brandForm.logo_url && (
                <div className="mt-2 flex items-center gap-2">
                  <img src={brandForm.logo_url} alt="Preview" className="w-12 h-12 rounded-lg object-cover bg-accent" onError={(e) => { e.target.style.display = 'none'; }} />
                  <span className="text-xs text-muted-foreground">Preview</span>
                </div>
              )}
            </div>

            <div className="pt-2 border-t border-border space-y-1">
              <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Rating Drop Alerts</p>
              <p className="text-[11px] text-muted-foreground">Daily check — if any SKU&apos;s live rating falls below the threshold, an email is sent to the brand manager.</p>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Brand Owner Email(s)</label>
              <Input
                type="text"
                placeholder="owner@brand.com, lead@brand.com"
                value={brandForm.manager_email}
                onChange={(e) => setBrandForm({ ...brandForm, manager_email: e.target.value })}
                data-testid="brand-manager-email-input"
              />
              <p className="text-[11px] text-muted-foreground">Brand owners receive alerts for <b>every</b> channel breach. Comma-separated for multiple recipients.</p>
            </div>

            {/* Channel-specific stakeholders */}
            {(editingBrand && (brandChannels.length > 0 || Object.keys(brandForm.channel_stakeholders || {}).length > 0)) && (
              <div className="space-y-2 pt-2 border-t border-border" data-testid="channel-stakeholders-block">
                <div className="flex items-baseline justify-between">
                  <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold flex items-center gap-1.5">
                    Channel Stakeholders
                    <button
                      type="button"
                      onClick={() => setShowRoutingInfo(true)}
                      className="relative inline-flex items-center justify-center w-4 h-4 rounded-full border border-[#2eb4ac]/40 text-[#2eb4ac] hover:bg-[#2eb4ac]/10 transition-colors group"
                      data-testid="routing-info-trigger"
                      title="How alerts route"
                    >
                      <span className="text-[10px] leading-none font-bold">i</span>
                      <span className="absolute inset-0 rounded-full border border-[#2eb4ac]/30 animate-ping pointer-events-none" />
                    </button>
                  </p>
                  <p className="text-[10px] text-muted-foreground">only get alerts for their channel</p>
                </div>
                {(() => {
                  // Render union of tracked channels + any channels that already have stakeholders saved
                  const tracked = new Set(brandChannels || []);
                  const saved = Object.keys(brandForm.channel_stakeholders || {});
                  saved.forEach(c => tracked.add(c));
                  const list = Array.from(tracked).sort();
                  if (list.length === 0) {
                    return <p className="text-[11px] italic text-muted-foreground">No channels tracked yet for this brand.</p>;
                  }
                  return list.map((ch) => (
                    <div key={ch} className="space-y-1">
                      <label className="text-xs font-medium flex items-center gap-1.5">
                        <Badge variant="outline" className="h-5 px-1.5 text-[10px]">{ch}</Badge>
                      </label>
                      <Input
                        type="text"
                        placeholder={`${ch.toLowerCase().replace(/[^a-z]/g,'')}-owner@brand.com`}
                        value={brandForm.channel_stakeholders?.[ch] || ''}
                        onChange={(e) => setBrandForm({
                          ...brandForm,
                          channel_stakeholders: { ...(brandForm.channel_stakeholders || {}), [ch]: e.target.value }
                        })}
                        data-testid={`channel-stakeholder-input-${ch}`}
                      />
                    </div>
                  ));
                })()}
              </div>
            )}
            <div className="space-y-2">
              <label className="text-sm font-medium">Alert Threshold (1.0 – 5.0)</label>
              <Input
                type="number"
                step="0.1"
                min="1"
                max="5"
                placeholder="4.3 (default)"
                value={brandForm.rating_threshold}
                onChange={(e) => setBrandForm({ ...brandForm, rating_threshold: e.target.value })}
                data-testid="brand-rating-threshold-input"
              />
              <p className="text-[11px] text-muted-foreground">SKUs with a live rating below this trigger an alert. Default is 4.3.</p>
            </div>

            {/* Alert schedule */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <label className="text-sm font-medium">Frequency</label>
                <select
                  className="w-full h-9 px-2 rounded-md border border-input bg-background text-sm"
                  value={brandForm.alert_frequency || 'daily'}
                  onChange={(e) => setBrandForm({ ...brandForm, alert_frequency: e.target.value })}
                  data-testid="brand-alert-frequency-select"
                >
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                </select>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Hour (IST)</label>
                <select
                  className="w-full h-9 px-2 rounded-md border border-input bg-background text-sm"
                  value={Number(brandForm.alert_hour_ist ?? 9)}
                  onChange={(e) => setBrandForm({ ...brandForm, alert_hour_ist: Number(e.target.value) })}
                  data-testid="brand-alert-hour-select"
                >
                  {Array.from({ length: 24 }, (_, h) => (
                    <option key={h} value={h}>
                      {(((h + 11) % 12) + 1)}:00 {h < 12 ? 'AM' : 'PM'} IST
                    </option>
                  ))}
                </select>
              </div>
            </div>
            {brandForm.alert_frequency === 'weekly' && (
              <div className="space-y-2">
                <label className="text-sm font-medium">Day of Week</label>
                <select
                  className="w-full h-9 px-2 rounded-md border border-input bg-background text-sm"
                  value={Number(brandForm.alert_day_of_week ?? 0)}
                  onChange={(e) => setBrandForm({ ...brandForm, alert_day_of_week: Number(e.target.value) })}
                  data-testid="brand-alert-dow-select"
                >
                  <option value={0}>Monday</option>
                  <option value={1}>Tuesday</option>
                  <option value={2}>Wednesday</option>
                  <option value={3}>Thursday</option>
                  <option value={4}>Friday</option>
                  <option value={5}>Saturday</option>
                  <option value={6}>Sunday</option>
                </select>
              </div>
            )}
            <p className="text-[11px] text-muted-foreground -mt-1">
              Emails fire automatically at the selected IST hour. Choose <b>Daily</b> for every-day or <b>Weekly</b> + a specific day.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBrandDialog(false)}>Cancel</Button>
            <Button onClick={saveBrand} className="bg-[#8b5cf6] hover:bg-[#8b5cf6]/90" data-testid="save-brand-btn">{editingBrand ? 'Update' : 'Add Brand'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Routing Info Dialog — futuristic visual cue */}
      <Dialog open={showRoutingInfo} onOpenChange={setShowRoutingInfo}>
        <DialogContent
          className="max-w-2xl border-[#2eb4ac]/20 bg-[#0a0f1e] text-slate-100 p-0 overflow-hidden"
          data-testid="routing-info-dialog"
        >
          {/* animated gradient scanline backdrop */}
          <div className="absolute inset-0 opacity-50 pointer-events-none" aria-hidden>
            <div className="absolute -top-32 -left-32 w-96 h-96 rounded-full bg-[#2eb4ac]/15 blur-3xl" />
            <div className="absolute -bottom-32 -right-32 w-96 h-96 rounded-full bg-[#8b5cf6]/15 blur-3xl" />
            <div className="absolute inset-0" style={{ backgroundImage: 'linear-gradient(rgba(46,180,172,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(46,180,172,0.06) 1px, transparent 1px)', backgroundSize: '32px 32px' }} />
          </div>

          <div className="relative z-10 p-7">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-slate-100 text-lg">
                <span className="inline-flex items-center justify-center w-7 h-7 rounded-md bg-gradient-to-br from-[#2eb4ac] to-[#0f766e] shadow-[0_0_24px_rgba(46,180,172,0.4)]">
                  <Sparkles className="w-4 h-4 text-white" />
                </span>
                How rating alerts route
              </DialogTitle>
              <p className="text-xs text-slate-400 mt-1">A single breach can trigger multiple targeted emails. Here&apos;s exactly who gets what.</p>
            </DialogHeader>

            {/* Scenario chip */}
            <div className="mt-6 mb-4 flex items-center gap-2 text-[11px] text-slate-400">
              <span className="px-2 py-0.5 rounded-full bg-[#2eb4ac]/10 text-[#2eb4ac] border border-[#2eb4ac]/30 font-mono tracking-wider uppercase">scenario</span>
              <span>Dynamite has SKUs breaching threshold on <b className="text-[#fbbf24]">Amazon</b> &amp; <b className="text-[#a78bfa]">Zepto</b> today.</span>
            </div>

            {/* Visual diagram */}
            <div className="grid grid-cols-12 gap-3 items-stretch">
              {/* Left column: breached channels */}
              <div className="col-span-3 space-y-2">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold pl-1">Breaches</div>
                <div className="rounded-xl border border-[#fbbf24]/40 bg-[#fbbf24]/5 px-3 py-2.5 backdrop-blur-sm shadow-[0_0_18px_rgba(251,191,36,0.15)]">
                  <div className="text-[10px] uppercase tracking-wider text-[#fbbf24] font-bold">Amazon</div>
                  <div className="text-[11px] text-slate-300 mt-0.5">5 SKUs &lt; 4.3</div>
                </div>
                <div className="rounded-xl border border-[#a78bfa]/40 bg-[#a78bfa]/5 px-3 py-2.5 backdrop-blur-sm shadow-[0_0_18px_rgba(167,139,250,0.15)]">
                  <div className="text-[10px] uppercase tracking-wider text-[#a78bfa] font-bold">Zepto</div>
                  <div className="text-[11px] text-slate-300 mt-0.5">3 SKUs &lt; 4.3</div>
                </div>
                <div className="rounded-xl border border-slate-700 bg-slate-800/30 px-3 py-2.5 opacity-40">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Flipkart</div>
                  <div className="text-[11px] text-slate-500 mt-0.5">no breaches</div>
                </div>
              </div>

              {/* Middle: animated connector lines */}
              <div className="col-span-2 relative flex items-stretch">
                <svg viewBox="0 0 100 200" preserveAspectRatio="none" className="w-full h-full">
                  <defs>
                    <linearGradient id="amazonFlow" x1="0" y1="0" x2="1" y2="0">
                      <stop offset="0%" stopColor="#fbbf24" stopOpacity="0.7" />
                      <stop offset="100%" stopColor="#2eb4ac" stopOpacity="0.4" />
                    </linearGradient>
                    <linearGradient id="zeptoFlow" x1="0" y1="0" x2="1" y2="0">
                      <stop offset="0%" stopColor="#a78bfa" stopOpacity="0.7" />
                      <stop offset="100%" stopColor="#2eb4ac" stopOpacity="0.4" />
                    </linearGradient>
                  </defs>
                  {/* Amazon -> Saurabh & Priya */}
                  <path d="M 0 30 C 35 30, 65 18, 100 18" stroke="url(#amazonFlow)" strokeWidth="1.5" fill="none" strokeDasharray="3 3">
                    <animate attributeName="stroke-dashoffset" from="6" to="0" dur="1.2s" repeatCount="indefinite" />
                  </path>
                  <path d="M 0 30 C 35 30, 65 70, 100 70" stroke="url(#amazonFlow)" strokeWidth="1.5" fill="none" strokeDasharray="3 3">
                    <animate attributeName="stroke-dashoffset" from="6" to="0" dur="1.2s" repeatCount="indefinite" />
                  </path>
                  {/* Zepto -> Saurabh & Swati */}
                  <path d="M 0 100 C 35 100, 65 18, 100 18" stroke="url(#zeptoFlow)" strokeWidth="1.5" fill="none" strokeDasharray="3 3">
                    <animate attributeName="stroke-dashoffset" from="6" to="0" dur="1.4s" repeatCount="indefinite" />
                  </path>
                  <path d="M 0 100 C 35 100, 65 130, 100 130" stroke="url(#zeptoFlow)" strokeWidth="1.5" fill="none" strokeDasharray="3 3">
                    <animate attributeName="stroke-dashoffset" from="6" to="0" dur="1.4s" repeatCount="indefinite" />
                  </path>
                </svg>
              </div>

              {/* Right column: recipients */}
              <div className="col-span-7 space-y-2">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold pl-1">Inboxes</div>
                {/* Saurabh - Brand owner */}
                <div className="rounded-xl border border-[#2eb4ac]/40 bg-gradient-to-br from-[#2eb4ac]/10 to-transparent px-3 py-2.5 backdrop-blur-sm">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="inline-flex w-2 h-2 rounded-full bg-[#2eb4ac] animate-pulse" />
                      <span className="text-[12px] font-semibold text-slate-100">saurabh@</span>
                      <span className="text-[9px] uppercase tracking-wider text-[#2eb4ac] bg-[#2eb4ac]/15 px-1.5 py-0.5 rounded-sm font-bold">Brand Owner</span>
                    </div>
                    <span className="text-[10px] text-slate-400">2 emails</span>
                  </div>
                  <div className="mt-1.5 flex gap-1.5">
                    <span className="text-[9px] px-1.5 py-0.5 rounded-sm bg-[#fbbf24]/15 text-[#fbbf24] font-mono">Amazon</span>
                    <span className="text-[9px] px-1.5 py-0.5 rounded-sm bg-[#a78bfa]/15 text-[#a78bfa] font-mono">Zepto</span>
                  </div>
                </div>
                {/* Priya - Amazon owner */}
                <div className="rounded-xl border border-[#fbbf24]/30 bg-[#fbbf24]/5 px-3 py-2.5 backdrop-blur-sm">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="inline-flex w-2 h-2 rounded-full bg-[#fbbf24]" />
                      <span className="text-[12px] font-semibold text-slate-100">priya@</span>
                      <span className="text-[9px] uppercase tracking-wider text-[#fbbf24] bg-[#fbbf24]/15 px-1.5 py-0.5 rounded-sm font-bold">Amazon Owner</span>
                    </div>
                    <span className="text-[10px] text-slate-400">1 email</span>
                  </div>
                  <div className="mt-1.5 flex gap-1.5">
                    <span className="text-[9px] px-1.5 py-0.5 rounded-sm bg-[#fbbf24]/15 text-[#fbbf24] font-mono">Amazon</span>
                  </div>
                </div>
                {/* Swati - Zepto owner */}
                <div className="rounded-xl border border-[#a78bfa]/30 bg-[#a78bfa]/5 px-3 py-2.5 backdrop-blur-sm">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="inline-flex w-2 h-2 rounded-full bg-[#a78bfa]" />
                      <span className="text-[12px] font-semibold text-slate-100">swati@</span>
                      <span className="text-[9px] uppercase tracking-wider text-[#a78bfa] bg-[#a78bfa]/15 px-1.5 py-0.5 rounded-sm font-bold">Zepto Owner</span>
                    </div>
                    <span className="text-[10px] text-slate-400">1 email</span>
                  </div>
                  <div className="mt-1.5 flex gap-1.5">
                    <span className="text-[9px] px-1.5 py-0.5 rounded-sm bg-[#a78bfa]/15 text-[#a78bfa] font-mono">Zepto</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Rules */}
            <div className="mt-7 grid grid-cols-3 gap-3 text-[11px]">
              <div className="rounded-lg border border-slate-800 bg-slate-900/60 p-3">
                <div className="flex items-center gap-1.5 text-[#2eb4ac] font-semibold mb-1">
                  <CheckCircle2 className="w-3 h-3" /> Brand Owners
                </div>
                <p className="text-slate-400 leading-snug">Receive every channel breach for the brand — one email per channel.</p>
              </div>
              <div className="rounded-lg border border-slate-800 bg-slate-900/60 p-3">
                <div className="flex items-center gap-1.5 text-[#fbbf24] font-semibold mb-1">
                  <Bell className="w-3 h-3" /> Channel Owners
                </div>
                <p className="text-slate-400 leading-snug">Only get emails for their own channel. Never see other channels.</p>
              </div>
              <div className="rounded-lg border border-slate-800 bg-slate-900/60 p-3">
                <div className="flex items-center gap-1.5 text-[#a78bfa] font-semibold mb-1">
                  <Send className="w-3 h-3" /> Routing
                </div>
                <p className="text-slate-400 leading-snug">1 email per breached channel. Brand + Channel owners share the <span className="font-mono">To:</span>.</p>
              </div>
            </div>

            <DialogFooter className="mt-7">
              <Button onClick={() => setShowRoutingInfo(false)} className="bg-[#2eb4ac] hover:bg-[#2eb4ac]/90 text-white" data-testid="routing-info-close">
                Got it
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>


      {/* Platform Dialog */}
      <Dialog open={showPlatformDialog} onOpenChange={setShowPlatformDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingPlatform ? 'Edit Platform' : 'Add Platform'}</DialogTitle>
            <DialogDescription>Enter platform details</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Platform Name</label>
              <Input placeholder="e.g., Amazon" value={platformForm.name} onChange={(e) => setPlatformForm({ ...platformForm, name: e.target.value })} data-testid="platform-name-input" />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium flex items-center gap-1"><Image className="w-4 h-4" /> Icon URL</label>
              <Input placeholder="https://example.com/icon.png" value={platformForm.icon_url} onChange={(e) => setPlatformForm({ ...platformForm, icon_url: e.target.value })} data-testid="platform-icon-input" />
              {platformForm.icon_url && (
                <div className="mt-2 flex items-center gap-2">
                  <img src={platformForm.icon_url} alt="Preview" className="w-12 h-12 rounded-lg object-cover bg-accent" onError={(e) => { e.target.style.display = 'none'; }} />
                  <span className="text-xs text-muted-foreground">Preview</span>
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPlatformDialog(false)}>Cancel</Button>
            <Button onClick={savePlatform} className="bg-[#2eb4ac] hover:bg-[#25908a]" data-testid="save-platform-btn">{editingPlatform ? 'Update' : 'Add Platform'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
